/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { ReactComponent as PhoneIcon } from 'assets/phone-icon.svg';
import { AuthorizationBackButton } from 'components/common/AuthorizationBackButton/AuthorizationBackButton';
import { ConfirmCodeWrapper } from 'components/common/ConfirmCodeWrapper/ConfirmCodeWrapper';
import { SMS_ERROR_CODE_DISABLE } from 'components/common/constants';
import { getTimerDurationSecondsLeft } from 'components/utils';
import ComplexTranslate from 'components/ComplexTranslate';
import { getErrorMessageForAlert } from 'services/utils';
import registrationStore from 'stores/registrationStore';

const Sms = ({
  sms,
  handleSms,
  resendCode,
  phone,
  error,
  isRegistration,
  isForgotPassword,
  authStore,
  passwordRecoveryStore
}) => {
  const i18n = useContext(i18nContext);
  const [showCountDown, setShowCountDown] = useState(false);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [isDisabled, setIsDisabled] = useState(false);
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL;

  const handleSwitchToAnotherMethod = () =>
    isForgotPassword
      ? passwordRecoveryStore.setIsShowSwitchConfirmationMethodScreen(true)
      : authStore.setIsShowSwitchConfirmationMethodScreen(true);

  useEffect(() => {
    if (SMS_ERROR_CODE_DISABLE.includes(error?.code)) {
      setIsDisabled(true);
      handleSms(sms, true);
    }
    error && setAlertState({ type: 'warning', message: getErrorMessageForAlert(i18n, error) });
  }, [error]);

  useEffect(() => {
    setShowCountDown(true);
  }, []);

  useEffect(() => {
    const store = isForgotPassword ? passwordRecoveryStore : authStore;
    const expiresAt = store.confirmationExpiresAt;
    if (!expiresAt) return;

    const secondsLeft = getTimerDurationSecondsLeft(expiresAt);

    const timerId = setTimeout(() => {
      setIsDisabled(true);
      setAlertState({ type: 'warning', message: i18n.getMessage('error.SECURITY_CODE_EXPIRED') });
    }, secondsLeft * 1000);

    return () => clearTimeout(timerId);
  }, [isForgotPassword ? passwordRecoveryStore.confirmationExpiresAt : authStore.confirmationExpiresAt]);

  const handleResendCode = async () => {
    setShowCountDown(true);
    await resendCode();
    isDisabled && setIsDisabled(false);
    setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
  };

  const onTimerEnd = () => {
    setShowCountDown(false);
  };

  const onComplete = (sms) => {
    handleSms(sms);
    setAlertState({ type: '', message: '' });
  };

  return (
    <div className={'authorization-form'} id={'form1'}>
      <PhoneIcon className={'svg-phone-icon registration-icon'} />
      <h1 className={'security-code-title'}>{i18n.getMessage('sms.title')}</h1>
      <ConfirmCodeWrapper
        PhoneComponent={<strong>{phone}</strong>}
        isDisabled={isDisabled}
        error={error}
        onComplete={onComplete}
        showCountDown={showCountDown}
        handleResendCode={handleResendCode}
        onTimerEnd={onTimerEnd}
        alertState={alertState}
        isSwitchToAnotherConfirmationMethod={!isRegistration && !isForgotPassword}
        handleSwitchToAnotherMethod={handleSwitchToAnotherMethod}
      />
      {isRegistration ? (
        <div className={'back-to-previous-step'}>
          <ComplexTranslate
            text={'register.backToUserRegistration'}
            interpolation={{
              previousStep: (
                <button
                  className={'unset link-normal'}
                  onClick={() => {
                    registrationStore.changePhoneNumber();
                  }}
                >
                  {i18n.getMessage('register.previousStep')}
                </button>
              )
            }}
          />
        </div>
      ) : null}
      <AuthorizationBackButton
        type={'login'}
        isGoogle2FAAuth={isForgotPassword ? passwordRecoveryStore.isGoogle2FAEnabled : authStore.isGoogle2FAEnabled}
        confirmationType={
          isForgotPassword ? passwordRecoveryStore.confirmationActionType : authStore.confirmationActionType
        }
      />
      {isForgotPassword && (
        <p className={'forgot-password-info-text'}>
          {i18n.getMessage('sms.forgotPassword.info2')}
          {supportEmail && (
            <a className={'forgot-password-info-link'} href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          )}
        </p>
      )}
    </div>
  );
};

Sms.propTypes = {
  sms: PropTypes.string.isRequired,
  phone: PropTypes.string,
  handleSms: PropTypes.func.isRequired,
  resendCode: PropTypes.func.isRequired,
  error: PropTypes.any,
  isRegistration: PropTypes.bool,
  isForgotPassword: PropTypes.bool,
  authStore: MobXPropTypes.observableObject,
  passwordRecoveryStore: MobXPropTypes.observableObject
};

export default inject('authStore', 'passwordRecoveryStore')(observer(Sms));
